import { CurrencyPipe, PercentPipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Input,
  OnInit,
  output,
  signal,
} from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { AdStatsEx, Marketplaces, StrategyEx } from "@front/m19-models";
import { AccountSelectionService, AuthService, StatsService, UserSelectionService } from "@front/m19-services";
import { ISelectMenuImports, IToggleComponent } from "@front/m19-ui";
import { DateUtils, MapUtils, sumAdStats } from "@front/m19-utils";
import { TranslocoDirective } from "@jsverse/transloco";
import { DaypartingConfig, DaypartingService } from "@m19-board/services/dayparting.service";
import { SettingRowComponent } from "@m19-board/shared/strategy-advanced-settings/setting-row/setting-row.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { DateTime } from "luxon";
import { combineLatest, filter, switchMap } from "rxjs";

const DEFAULT_PAUSE_HOUR = 22;
const DEFAULT_REACTIVATION_HOUR = 8;
const HOURS = Array.from(Array(24).keys());

interface DaypartingSavings {
  totalCost: number;
  totalAdSales: number;
  totalCostOnPeriod: number;
  totalAdSalesOnPeriod: number;
}

@UntilDestroy()
@Component({
  imports: [
    PercentPipe,
    CurrencyPipe,
    FormsModule,
    TranslocoDirective,
    ISelectMenuImports,
    IToggleComponent,
    SettingRowComponent,
  ],
  selector: "app-day-parting-inputs",
  templateUrl: "./day-parting-inputs.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DayPartingInputsComponent implements OnInit {
  private accountMarketplaceSelection = inject(AccountSelectionService);
  private userSelectionService = inject(UserSelectionService);
  private statsService = inject(StatsService);

  private readonly authService = inject(AuthService);
  private readonly daypartingService = inject(DaypartingService);

  readonly enabledDayParting = signal<boolean | undefined>(undefined);
  readonly savings = signal<DaypartingSavings | undefined>(undefined);
  dataByHour?: Map<number, AdStatsEx>;
  allStratData: AdStatsEx[] = [];
  readonly HOURS = HOURS;
  organizationId!: number;

  strategy = input.required<StrategyEx>();
  spStrategy = computed(() => {
    const strategy = this.strategy();
    return strategy.isSpStrategy() ? strategy : undefined;
  });
  strategy$ = toObservable(this.spStrategy);

  readonly daypartingPauseHour = signal<number | undefined>(undefined);
  readonly daypartingReactivationHour = signal<number | undefined>(undefined);
  @Input() locale?: string;
  readonly currencyCode = toSignal(this.userSelectionService.selectedCurrency$);
  @Input() placeholderPause?: number;
  @Input() placeholderReactivation?: number;
  @Input() placeholderSwitch?: boolean;
  @Input() disabled = false;
  @Input() textSize: string = "";
  isInModal = input<boolean>(false);
  daypartingConfig = output<DaypartingConfig>();

  private selectedAccountMarketplace = toSignal(this.accountMarketplaceSelection.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  private userLocale = toSignal(this.authService.userLocale$);

  readonly selectedTimeZone = computed(() => {
    const marketplace = Marketplaces[this.selectedAccountMarketplace().marketplace];
    return DateTime.local({ zone: marketplace.timeZone, locale: this.userLocale() });
  });

  readonly formattedTimeZone = computed(() => this.selectedTimeZone().toFormat("ZZZZ", { locale: this.userLocale() }));
  readonly timeZoneOffest = computed(() => DateUtils.getTimezoneOffset(this.selectedTimeZone().zoneName));

  constructor() {
    this.accountMarketplaceSelection.singleAccountMarketplaceSelection$.pipe(untilDestroyed(this)).subscribe((am) => {
      this.organizationId = am.resourceOrganizationId!;
    });
  }

  ngOnInit(): void {
    combineLatest([
      this.accountMarketplaceSelection.singleAccountMarketplaceSelection$,
      this.userSelectionService.selectedCurrency$,
    ])
      .pipe(
        untilDestroyed(this),
        switchMap(([am, currency]) =>
          this.statsService.getHourlyCampaignStats(
            am.accountId,
            am.marketplace,
            currency,
            DateUtils.formatDateForApiFromToday(-15),
            DateUtils.formatDateForApiFromToday(-1),
          ),
        ),
      )
      .subscribe((x) => {
        this.allStratData = x!;
        const data: AdStatsEx[] = this.allStratData.filter(
          (x) => x.strategyId != undefined && x.strategyId === this.strategy()?.strategyId,
        );
        this.dataByHour = new Map<number, AdStatsEx>();
        for (const stat of data) {
          MapUtils.insertInMap(this.dataByHour, stat.hour, stat, sumAdStats);
        }
        this.computeSavings();
      });

    this.strategy$
      .pipe(
        untilDestroyed(this),
        filter((s) => !!s),
      )
      .subscribe((strat) => {
        this.enabledDayParting.update(
          (enabled) => enabled ?? (strat.daypartingPauseHour != null && strat.daypartingReactivationHour != null),
        );
        this.daypartingPauseHour.update((hour) => hour ?? strat.daypartingPauseHour ?? DEFAULT_PAUSE_HOUR);
        this.daypartingReactivationHour.update(
          (hour) => hour ?? strat.daypartingReactivationHour ?? DEFAULT_REACTIVATION_HOUR,
        );

        const data: AdStatsEx[] = this.allStratData.filter(
          (x) => x.strategyId != undefined && x.strategyId === strat.strategyId,
        );
        this.dataByHour = new Map<number, AdStatsEx>();
        for (const stat of data) {
          MapUtils.insertInMap(this.dataByHour, stat.hour, stat, sumAdStats);
        }
        this.computeSavings();
      });

    if (this.placeholderPause != undefined) this.daypartingPauseHour.set(this.placeholderPause);
    if (this.placeholderReactivation != undefined) this.daypartingReactivationHour.set(this.placeholderReactivation);
    if (this.placeholderSwitch != undefined) this.enabledDayParting.set(this.placeholderSwitch);
    if (this.placeholderPause != undefined || this.placeholderReactivation != undefined) this.computeSavings();
  }

  private computeSavings(): void {
    const savings: DaypartingSavings = { totalCost: 0, totalAdSales: 0, totalCostOnPeriod: 0, totalAdSalesOnPeriod: 0 };
    const pauseHour = this.daypartingPauseHour()!;
    const reactivationHour = this.daypartingReactivationHour()!;

    this.dataByHour?.forEach((val) => {
      savings.totalCost += val.cost ?? 0;
      savings.totalAdSales += val.adSales ?? 0;
      if (DateUtils.isInHourInterval(val.hour!, pauseHour, reactivationHour)) {
        savings.totalCostOnPeriod += val.cost ?? 0;
        savings.totalAdSalesOnPeriod += val.adSales ?? 0;
      }
    });
    this.savings.set(savings);
  }

  selectDayPartingPauseHour(hour: number | undefined): void {
    if (hour == null) return;
    this.daypartingPauseHour.set(hour);
    this.switchDisableDayParting(true);
  }

  selectDayPartingReactivationHour(hour: number | undefined): void {
    if (hour == null) return;
    this.daypartingReactivationHour.set(hour);
    this.switchDisableDayParting(true);
  }

  switchDisableDayParting(enable: boolean) {
    if (this.daypartingPauseHour() === this.daypartingReactivationHour()) return;
    this.computeSavings();
    this.enabledDayParting.set(enable);
    this.pushDayPartingPref();
  }

  pushDayPartingPref(): void {
    const config: DaypartingConfig = {
      strategy: this.strategy(),
      organizationId: this.organizationId,
      daypartingPauseHour: this.daypartingPauseHour()!,
      daypartingReactivationHour: this.daypartingReactivationHour()!,
      enabledDayParting: this.enabledDayParting()!,
    };

    if (this.isInModal()) {
      this.daypartingConfig.emit(config);
      return;
    }

    this.daypartingService.saveConfig(config).subscribe();
  }
}
